/**
 * AONA INDIA - Firebase Integration Module
 * Handles Firebase Authentication (Email/Password, Google Sign-In, Phone OTP)
 * & Cloud Firestore database sync for aonaindia.com
 *
 * Configured using Firebase Agent Skills (firebase-auth-basics, firebase-firestore, firebase-basics)
 */

import { initializeApp, getApps, getApp } from "https://www.gstatic.com/firebasejs/10.8.0/firebase-app.js";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult, 
  GoogleAuthProvider, 
  RecaptchaVerifier, 
  signInWithPhoneNumber, 
  onAuthStateChanged, 
  updateProfile,
  signOut 
} from "https://www.gstatic.com/firebasejs/10.8.0/firebase-auth.js";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  serverTimestamp 
} from "https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js";

// Default Firebase Configuration for project: aona-login
// Can be customized via window.aonaFirebaseConfig Liquid settings
const defaultFirebaseConfig = {
  apiKey: window.aonaFirebaseConfig?.apiKey || "AIzaSyDWbLaTo7RGpkGO-MTu30Yzy28YHNRzto8",
  authDomain: window.aonaFirebaseConfig?.authDomain || "aona-login.firebaseapp.com",
  projectId: window.aonaFirebaseConfig?.projectId || "aona-login",
  storageBucket: window.aonaFirebaseConfig?.storageBucket || "aona-login.firebasestorage.app",
  messagingSenderId: window.aonaFirebaseConfig?.messagingSenderId || "742853812035",
  appId: window.aonaFirebaseConfig?.appId || "1:742853812035:web:86b45e9b52451bda9fd9f6",
  measurementId: window.aonaFirebaseConfig?.measurementId || "G-S68HVXQZZR"
};

function sanitizeText(str) {
  if (!str) return '';
  return String(str).replace(/[<>'"`;]/g, '').trim().slice(0, 255);
}

class AonaFirebaseController {
  constructor() {
    this.app = null;
    this.auth = null;
    this.db = null;
    this.recaptchaVerifier = null;
    this.confirmationResult = null;
    this.init();
  }

  async init() {
    try {
      this.app = !getApps().length ? initializeApp(defaultFirebaseConfig) : getApp();
      this.auth = getAuth(this.app);
      this.db = getFirestore(this.app);
      window.aonaFirebaseAuth = this.auth;
      window.aonaFirestore = this.db;
      console.log("[AONA Firebase] Initialized successfully for project: aona-login");
      
      this.listenToAuthState();
      await this.checkRedirectResult();
      await this.completeEmailLinkSignIn();
    } catch (err) {
      console.warn("[AONA Firebase] Initialization error:", err.message);
    }
  }

  // Check for result if sign in used redirect mode
  async checkRedirectResult() {
    if (!this.auth) return;
    try {
      const result = await getRedirectResult(this.auth);
      if (result && result.user) {
        console.log("[AONA Firebase] Redirect Sign-In success:", result.user.email);
        await this.syncUserToFirestore(result.user);
        
        var pending = sessionStorage.getItem('aona_pending_cart_action');
        if (pending) {
          sessionStorage.removeItem('aona_pending_cart_action');
          try {
            var pItem = JSON.parse(pending);
            if (pItem && pItem.id) {
              await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: pItem.id, quantity: pItem.quantity || 1 })
              });
            }
          } catch(e) {}
        }

        var isBuyNow = false;
        try {
          isBuyNow = (sessionStorage.getItem('aona_pending_buy_now') === 'true') ||
                     (document.cookie.indexOf('aona_buy_now=true') !== -1);
        } catch(e) {}

        const urlParams = new URLSearchParams(window.location.search);
        const defaultDest = '/checkout';
        const rawTarget = (urlParams.get('checkout_url') || urlParams.get('return_to') || defaultDest).trim();
        const safeTarget = (rawTarget.indexOf('/') === 0 && rawTarget.indexOf('//') !== 0 && !rawTarget.includes('://') && rawTarget !== '/account' && rawTarget !== '/account/login') ? rawTarget : defaultDest;
        if (isBuyNow) {
          try {
            sessionStorage.removeItem('aona_pending_buy_now');
            document.cookie = 'aona_buy_now=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
          } catch(e) {}
        }
        window.location.href = safeTarget;
      }
    } catch (err) {
      console.error("[AONA Firebase] Redirect result error:", err.code, err.message);
    }
  }

  // 1. Observe Authentication State Changes
  listenToAuthState() {
    if (!this.auth) return;
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        // STRICT: If user has an unverified email, DO NOT activate the session!
        const isPasswordProvider = user.providerData?.some(p => p.providerId === 'password');
        if (isPasswordProvider && !user.emailVerified) {
          console.log("[AONA Firebase] User email unverified. Auth state ignored for:", user.email);
          return;
        }

        console.log("[AONA Firebase] User authenticated & verified:", user.uid, user.email || user.phoneNumber);
        this.saveLocalSession(user);
        await this.syncUserToFirestore(user);
        this.updateUIForSignedInUser(user);
        if (typeof window.aonaSyncHeaderAuth === 'function') {
          window.aonaSyncHeaderAuth();
        }
      } else {
        console.log("[AONA Firebase] User signed out");
        try {
          localStorage.removeItem('aona_firebase_user');
          sessionStorage.removeItem('aona_firebase_user');
          localStorage.removeItem('aona_customer_email');
        } catch(e) {}
      }
    });
  }

  saveLocalSession(user, additionalData = {}) {
    if (!user) return;
    const isPasswordProvider = user.providerData?.some(p => p.providerId === 'password');
    if (isPasswordProvider && !user.emailVerified) {
      console.warn("[AONA Firebase] Refusing to save local session for unverified email user:", user.email);
      return;
    }

    const rawNames = (user.displayName || "").split(" ");
    const safeFirstName = sanitizeText(additionalData.firstName || rawNames[0] || "");
    const safeLastName = sanitizeText(additionalData.lastName || rawNames.slice(1).join(" ") || "");
    const safeDisplayName = sanitizeText(user.displayName || `${safeFirstName} ${safeLastName}`.trim());
    const safeEmail = sanitizeText(user.email || "");
    const safePhone = sanitizeText(user.phoneNumber || "");

    const profile = {
      uid: user.uid,
      email: safeEmail,
      phoneNumber: safePhone,
      displayName: safeDisplayName,
      firstName: safeFirstName,
      lastName: safeLastName,
      photoURL: user.photoURL || "",
      authProvider: user.providerData?.[0]?.providerId || (safePhone ? "phone" : "google.com"),
      emailVerified: Boolean(user.emailVerified),
      timestamp: Date.now()
    };
    try {
      localStorage.setItem('aona_firebase_user', JSON.stringify(profile));
      sessionStorage.setItem('aona_firebase_user', JSON.stringify(profile));
      if (safeEmail) localStorage.setItem('aona_customer_email', safeEmail);
    } catch (e) {}
  }

  // 2. Sync User Profile to Firestore Database & Shopify Customer Database
  async syncUserToFirestore(user, additionalData = {}) {
    if (!user) return;

    // STRICT: Only sync and create Shopify customer IF user's email is verified or provider is Google
    const isPasswordProvider = user.providerData?.some(p => p.providerId === 'password');
    if (isPasswordProvider && !user.emailVerified) {
      console.warn("[AONA Firebase] Refusing to sync unverified customer to Shopify/Firestore:", user.email);
      return;
    }
    
    this.saveLocalSession(user, additionalData);

    const rawNames = (user.displayName || "").split(" ");
    const safeFirstName = sanitizeText(additionalData.firstName || rawNames[0] || "");
    const safeLastName = sanitizeText(additionalData.lastName || rawNames.slice(1).join(" ") || "");
    const safeDisplayName = sanitizeText(user.displayName || `${safeFirstName} ${safeLastName}`.trim());
    const safeEmail = sanitizeText(user.email || "");
    const safePhone = sanitizeText(user.phoneNumber || "");

    // A. Sync to Shopify Admin > Customers Database
    if (safeEmail) {
      await this.createShopifyCustomer(safeEmail, safeFirstName, safeLastName);
    }

    // B. Sync to Firebase Firestore if initialized
    if (this.db) {
      try {
        const userRef = doc(this.db, "users", user.uid);
        const userData = {
          uid: user.uid,
          email: safeEmail,
          phoneNumber: safePhone,
          displayName: safeDisplayName,
          lastLoginAt: serverTimestamp(),
          authProvider: user.providerData?.[0]?.providerId || "google.com",
          store: "aonaindia.com"
        };
        await setDoc(userRef, userData, { merge: true });
        console.log("[AONA Firebase] Synced user profile to Firestore:", user.uid);
      } catch (err) {
        console.warn("[AONA Firebase] Firestore sync note:", err.message);
      }
    }
  }

  // 2B. Automatically create/update Customer in Shopify Admin Database
  async createShopifyCustomer(email, firstName = "", lastName = "") {
    try {
      const formData = new FormData();
      formData.append('form_type', 'create_customer');
      formData.append('utf8', '✓');
      formData.append('customer[email]', email);
      if (firstName) formData.append('customer[first_name]', firstName);
      if (lastName) formData.append('customer[last_name]', lastName);

      await fetch('/account', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'text/html,application/xhtml+xml' }
      });
      console.log('[AONA Firebase] Customer registered in Shopify database:', email);
    } catch (err) {
      console.warn('[AONA Firebase] Shopify customer sync note:', err.message);
    }
  }

  // 3. Email & Password Sign In (Strictly Enforces Verified Email)
  async loginWithEmail(email, password) {
    if (!this.auth) throw new Error("Firebase Auth is not initialized. Please verify SDK credentials.");
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;
      
      // Reload user from Firebase servers to fetch freshest emailVerified status
      await user.reload();

      // Enforce email verification for email/password credentials
      if (!user.emailVerified) {
        // Sign out immediately so unverified session is not saved
        await signOut(this.auth);
        try {
          localStorage.removeItem('aona_firebase_user');
          sessionStorage.removeItem('aona_firebase_user');
        } catch(e) {}

        const unverifiedErr = new Error("Please verify your email address. A verification link was sent to your Gmail inbox.");
        unverifiedErr.code = "auth/unverified-email";
        unverifiedErr.user = user;
        unverifiedErr.email = email;
        throw unverifiedErr;
      }

      this.saveLocalSession(user);
      await this.syncUserToFirestore(user);
      return user;
    } catch (error) {
      console.error("[AONA Firebase] Email sign in error:", error.code, error.message);
      throw this.formatAuthError(error);
    }
  }

  // 4. Email & Password Registration (Dispatches verification email to Gmail, blocks immediate login)
  async registerWithEmail(email, password, firstName = "", lastName = "") {
    if (!this.auth) throw new Error("Firebase Auth is not initialized. Please verify SDK credentials.");
    const trimmedEmail = (email || '').trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      const emailErr = new Error("Please enter a valid email address.");
      emailErr.code = "auth/invalid-email";
      throw emailErr;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, trimmedEmail, password);
      const user = userCredential.user;

      // Update Firebase profile display name
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        try {
          await updateProfile(user, { displayName: fullName });
        } catch(e) {}
      }

      // Action code settings - redirect to login page after verifying in Gmail
      const actionCodeSettings = {
        url: window.location.origin + '/account/login?email_verified=true',
        handleCodeInApp: false
      };

      // Send Firebase Email Verification link to their Gmail
      try {
        await sendEmailVerification(user, actionCodeSettings);
        console.log("[AONA Firebase] Verification link dispatched to Gmail:", email);
      } catch (emailErr) {
        console.warn("[AONA Firebase] sendEmailVerification with actionCodeSettings notice:", emailErr.message, "Retrying default handler...");
        try {
          await sendEmailVerification(user);
          console.log("[AONA Firebase] Verification link dispatched via default handler to Gmail:", email);
        } catch (fallbackErr) {
          console.error("[AONA Firebase] sendEmailVerification fallback failed:", fallbackErr.message);
          throw fallbackErr;
        }
      }

      // CRITICAL: Sign out immediately so unverified user cannot access account
      await signOut(this.auth);
      try {
        localStorage.removeItem('aona_firebase_user');
        sessionStorage.removeItem('aona_firebase_user');
      } catch(e) {}

      return {
        email: email,
        displayName: fullName
      };
    } catch (error) {
      console.error("[AONA Firebase] Registration error:", error.code, error.message);
      throw this.formatAuthError(error);
    }
  }

  // 4B. Resend Verification Email to Gmail
  async resendVerificationEmail(email, password) {
    if (!this.auth) throw new Error("Firebase Auth is not initialized.");
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;
      const actionCodeSettings = {
        url: window.location.origin + '/account/login?email_verified=true',
        handleCodeInApp: false
      };
      try {
        await sendEmailVerification(user, actionCodeSettings);
      } catch (actionErr) {
        console.warn("[AONA Firebase] Resend with actionCodeSettings notice, trying fallback:", actionErr.message);
        await sendEmailVerification(user);
      }
      await signOut(this.auth);
      console.log("[AONA Firebase] Verification link resent to Gmail:", email);
      return true;
    } catch (error) {
      console.error("[AONA Firebase] Resend verification error:", error);
      throw this.formatAuthError(error);
    }
  }

  // 4C. Check if current user's email has been verified
  async checkEmailVerified() {
    if (!this.auth || !this.auth.currentUser) return false;
    try {
      await this.auth.currentUser.reload();
      const isVerified = Boolean(this.auth.currentUser.emailVerified);
      if (isVerified) {
        this.saveLocalSession(this.auth.currentUser);
      }
      return isVerified;
    } catch (err) {
      console.warn("[AONA Firebase] Check email verified error:", err);
      return false;
    }
  }

  // 4D. Send Passwordless Sign-In Link to Email
  async sendEmailSignInLink(email) {
    if (!this.auth) throw new Error("Firebase Auth is not initialized.");
    const safeEmail = sanitizeText(email);
    const actionCodeSettings = {
      url: window.location.origin + '/account/login?email_link_auth=true',
      handleCodeInApp: true
    };
    await sendSignInLinkToEmail(this.auth, safeEmail, actionCodeSettings);
    try {
      localStorage.setItem('aona_email_for_sign_in', safeEmail);
    } catch(e) {}
    console.log("[AONA Firebase] Sign-in link dispatched to:", safeEmail);
    return true;
  }

  // 4E. Check and Complete Sign-In with Email Link
  async completeEmailLinkSignIn() {
    if (!this.auth) return null;
    try {
      if (isSignInWithEmailLink(this.auth, window.location.href)) {
        let email = null;
        try {
          email = localStorage.getItem('aona_email_for_sign_in') || localStorage.getItem('aona_pending_verify_email') || localStorage.getItem('aona_customer_email');
        } catch(e) {}
        if (!email) {
          // Use custom styled modal on mobile instead of native window.prompt
          if (typeof window.aonaEmailPrompt === 'function') {
            email = await window.aonaEmailPrompt();
          } else {
            email = window.prompt('Please confirm your email address to complete sign in:');
          }
        }
        if (!email) return null;
        
        console.log("[AONA Firebase] Completing email link sign-in for:", email);
        const result = await signInWithEmailLink(this.auth, email, window.location.href);
        if (result && result.user) {
          // STRICT: Clear the pending verification flag — email is now verified
          try {
            localStorage.removeItem('aona_email_for_sign_in');
            localStorage.removeItem('aona_pending_verify_email');
          } catch(e) {}

          // Mark the session as verified before saving
          const verifiedUser = result.user;
          // Force emailVerified = true in local session (email link auth inherently verifies)
          const profileOverride = {
            uid: verifiedUser.uid,
            email: verifiedUser.email || email,
            displayName: verifiedUser.displayName || '',
            emailVerified: true, // ← CRITICAL: always true after email link sign-in
            authProvider: 'emailLink',
            photoURL: verifiedUser.photoURL || '',
            timestamp: Date.now()
          };
          try {
            localStorage.setItem('aona_firebase_user', JSON.stringify(profileOverride));
            sessionStorage.setItem('aona_firebase_user', JSON.stringify(profileOverride));
            if (verifiedUser.email) localStorage.setItem('aona_customer_email', verifiedUser.email);
          } catch(e) {}

          await this.syncUserToFirestore(verifiedUser);
          console.log("[AONA Firebase] Email link sign in successful:", verifiedUser.email);

          // Clean up the oobCode/apiKey/etc from the URL before redirecting
          var isBuyNow = false;
          try {
            isBuyNow = (sessionStorage.getItem('aona_pending_buy_now') === 'true') ||
                       (document.cookie.indexOf('aona_buy_now=true') !== -1);
          } catch(e) {}

          const urlParams = new URLSearchParams(window.location.search);
          const defaultDest = '/checkout';
          const rawTarget = (urlParams.get('checkout_url') || urlParams.get('return_to') || defaultDest).trim();
          const safeTarget = (rawTarget.indexOf('/') === 0 && rawTarget.indexOf('//') !== 0 && !rawTarget.includes('://') && rawTarget !== '/account' && rawTarget !== '/account/login') ? rawTarget : defaultDest;
          if (isBuyNow) {
            try {
              sessionStorage.removeItem('aona_pending_buy_now');
              document.cookie = 'aona_buy_now=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
            } catch(e) {}
          }
          window.location.href = safeTarget;
          return verifiedUser;
        }
      }
    } catch (err) {
      console.error("[AONA Firebase] Email link sign in error:", err);
    }
    return null;
  }

  // 5. Google Sign-In with Popup & Automatic Redirect Fallback (Pure Firebase Auth SDK)
  async signInWithGoogle(useRedirectFallback = true) {
    if (!this.auth) throw new Error("Firebase Auth is not initialized. Please verify SDK credentials.");
    
    const provider = new GoogleAuthProvider();
    provider.addScope("email");
    provider.addScope("profile");
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      console.log("[AONA Firebase] Starting Google Sign-In popup via Firebase Auth...");
      const result = await signInWithPopup(this.auth, provider);
      if (result && result.user) {
        await this.syncUserToFirestore(result.user);
        var pending = sessionStorage.getItem('aona_pending_cart_action');
        if (pending) {
          sessionStorage.removeItem('aona_pending_cart_action');
          try {
            var pItem = JSON.parse(pending);
            if (pItem && pItem.id) {
              await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: pItem.id, quantity: pItem.quantity || 1 })
              });
            }
          } catch(e) {}
        }
        return result.user;
      }
      throw new Error("No user returned from Google authentication.");
    } catch (error) {
      console.error("[AONA Firebase] Google Sign-In error:", error.code, error.message);
      
      // If popup was blocked or closed, attempt redirect fallback
      if (useRedirectFallback && (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user')) {
        console.log("[AONA Firebase] Popup blocked or closed. Attempting signInWithRedirect fallback...");
        try {
          await signInWithRedirect(this.auth, provider);
          return null; // Will resume via checkRedirectResult on reload
        } catch (redirectErr) {
          console.error("[AONA Firebase] Redirect fallback error:", redirectErr);
          throw this.formatAuthError(redirectErr);
        }
      }

      throw this.formatAuthError(error);
    }
  }

  // 5B. Update User Profile Name & Sync
  async updateUserProfile(firstName, lastName) {
    if (!this.auth || !this.auth.currentUser) return null;
    const fullName = `${firstName} ${lastName}`.trim();
    try {
      await updateProfile(this.auth.currentUser, { displayName: fullName });
    } catch (err) {
      console.warn("[AONA Firebase] updateProfile warning:", err.message);
    }
    const user = this.auth.currentUser;
    await this.syncUserToFirestore(user, { firstName, lastName, displayName: fullName });
    this.saveLocalSession(user, { firstName, lastName });
    return user;
  }


  // Format friendly, actionable error messages
  formatAuthError(error) {
    if (!error) return new Error('An unexpected error occurred. Please try again.');
    if (error.code === 'auth/unverified-email') {
      return error;
    }
    var err = error;
    if (error.code === 'auth/email-already-in-use') {
      err = new Error('This email address is already registered. Please sign in or reset your password.');
    } else if (error.code === 'auth/weak-password') {
      err = new Error('Password should be at least 6 characters long.');
    } else if (error.code === 'auth/invalid-email') {
      err = new Error('Please enter a valid email address.');
    } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      err = new Error('Invalid email or password. Please verify your credentials or sign up.');
    } else if (error.code === 'auth/unauthorized-domain') {
      const currentHost = window.location.hostname;
      err = new Error(`Unauthorized Domain: "${currentHost}" is not added in Firebase Console. Please add "${currentHost}" under Firebase Console > Authentication > Settings > Authorized domains.`);
    } else if (error.code === 'auth/operation-not-allowed') {
      err = new Error('This sign-in method is not enabled in Firebase Console. Please check Authentication > Sign-in method.');
    } else if (error.code === 'auth/popup-closed-by-user') {
      err = new Error('The sign-in window was closed before completing authentication. Please try again.');
    } else if (error.code === 'auth/popup-blocked') {
      err = new Error('The pop-up window was blocked by your browser. Please allow pop-ups for this website.');
    } else if (error.code === 'auth/invalid-phone-number') {
      err = new Error('Invalid phone number format. Please ensure your 10-digit mobile number and country code are correct.');
    } else if (error.code === 'auth/quota-exceeded') {
      err = new Error('SMS quota reached for today. Please test with Firebase test numbers or use Google Sign-In.');
    } else if (error.code === 'auth/invalid-verification-code') {
      err = new Error('Incorrect 6-digit OTP code. Please check your SMS and enter the code again.');
    } else if (error.code === 'auth/code-expired') {
      err = new Error('Verification code has expired. Please click "Resend Code" to get a new OTP.');
    } else if (error.code === 'auth/too-many-requests') {
      err = new Error('Too many attempts. Please wait a couple of minutes and try again.');
    } else if (error.code === 'auth/captcha-check-failed') {
      err = new Error('Security check failed. Please refresh the page and try again.');
    }
    err.code = error.code;
    return err;
  }

  // 6. Phone OTP Authentication — Step A: Send Verification Code
  async sendPhoneOTP(phoneNumber, buttonOrContainerId = "aona-recaptcha-container") {
    if (!this.auth) throw new Error("Firebase Auth is not initialized. Please verify SDK credentials.");
    try {
      if (!this.recaptchaVerifier) {
        this.recaptchaVerifier = new RecaptchaVerifier(this.auth, buttonOrContainerId, {
          size: "invisible",
          callback: (response) => {
            console.log("[AONA Firebase] Invisible reCAPTCHA verified");
          },
          'expired-callback': () => {
            console.log("[AONA Firebase] reCAPTCHA expired, resetting...");
          }
        });
      }
      this.confirmationResult = await signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier);
      console.log("[AONA Firebase] OTP SMS dispatched to:", phoneNumber);
      return true;
    } catch (error) {
      console.error("[AONA Firebase] Phone OTP error:", error.code, error.message);
      if (this.recaptchaVerifier) {
        try {
          this.recaptchaVerifier.clear();
          this.recaptchaVerifier = null;
        } catch (e) {}
      }
      throw this.formatAuthError(error);
    }
  }

  // 6. Phone OTP Authentication — Step B: Confirm Verification Code
  async verifyPhoneOTP(otpCode) {
    if (!this.confirmationResult) throw new Error("No active OTP session. Please request a new verification code.");
    try {
      const result = await this.confirmationResult.confirm(otpCode);
      await this.syncUserToFirestore(result.user);
      return result.user;
    } catch (error) {
      console.error("[AONA Firebase] OTP confirmation failed:", error.code, error.message);
      throw this.formatAuthError(error);
    }
  }

  // 7. Sign Out
  async logout() {
    try {
      localStorage.removeItem('aona_firebase_user');
      sessionStorage.removeItem('aona_firebase_user');
      localStorage.removeItem('aona_customer_email');
    } catch (e) {}
    if (this.auth) {
      await signOut(this.auth);
    }
  }

  updateUIForSignedInUser(user) {
    const userDisplay = document.querySelectorAll(".aona-firebase-user-name");
    userDisplay.forEach(el => {
      el.textContent = user.displayName || user.email || user.phoneNumber || "Account";
    });
  }

  // 8. Order Management & Firestore Cloud Sync for aonaindia.com
  async saveUserOrder(orderData) {
    if (!orderData) return;
    try {
      var email = (orderData.email || localStorage.getItem('aona_customer_email') || '').trim().toLowerCase();
      var uid = (this.auth && this.auth.currentUser) ? this.auth.currentUser.uid : null;
      var rawId = String(orderData.id || orderData.name || Date.now());
      var cleanId = rawId.replace(/[^a-zA-Z0-9_-]/g, '');

      // Local storage persistence
      var localOrders = [];
      try {
        if (email) {
          localOrders = JSON.parse(localStorage.getItem('aona_orders_' + email) || '[]');
        }
        if (!localOrders.length) {
          localOrders = JSON.parse(localStorage.getItem('aona_recent_orders') || '[]');
        }
      } catch(e) {}

      localOrders = localOrders.filter(function(o) { return o.id !== orderData.id && o.name !== orderData.name; });
      localOrders.unshift(orderData);
      
      if (email) {
        localStorage.setItem('aona_orders_' + email, JSON.stringify(localOrders.slice(0, 20)));
      }
      localStorage.setItem('aona_recent_orders', JSON.stringify(localOrders.slice(0, 20)));

      // Firestore Cloud sync
      if (this.db) {
        var docRef = doc(this.db, "orders", cleanId);
        var cloudData = {
          ...orderData,
          email: email,
          userId: uid,
          updatedAt: serverTimestamp()
        };
        await setDoc(docRef, cloudData, { merge: true });
        console.log("[AONA Firebase] Order saved to Firestore:", cleanId);
      }
    } catch(err) {
      console.warn("[AONA Firebase] Order save note:", err.message);
    }
  }

  async fetchUserOrders(email, uid) {
    var orders = [];
    var safeEmail = (email || '').trim().toLowerCase();

    // 1. Check local storage first
    try {
      if (safeEmail) {
        var saved = localStorage.getItem('aona_orders_' + safeEmail);
        if (saved) orders = JSON.parse(saved);
      }
      if (!orders.length) {
        var recent = localStorage.getItem('aona_recent_orders');
        if (recent) orders = JSON.parse(recent);
      }
    } catch(e) {}

    // 2. Fetch from Firestore if initialized
    if (this.db && (safeEmail || uid)) {
      try {
        var { collection, query, where, getDocs } = await import("https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js");
        var ordersRef = collection(this.db, "orders");
        var q = safeEmail ? query(ordersRef, where("email", "==", safeEmail)) : query(ordersRef, where("userId", "==", uid));
        var snap = await getDocs(q);
        var cloudOrders = [];
        snap.forEach(function(d) {
          cloudOrders.push(d.data());
        });
        if (cloudOrders.length > 0) {
          cloudOrders.forEach(function(co) {
            if (!orders.some(function(lo) { return lo.id === co.id || lo.name === co.name; })) {
              orders.push(co);
            }
          });
          if (safeEmail) {
            localStorage.setItem('aona_orders_' + safeEmail, JSON.stringify(orders));
          }
          localStorage.setItem('aona_recent_orders', JSON.stringify(orders));
        }
      } catch(err) {
        console.warn("[AONA Firebase] Firestore fetch orders note:", err.message);
      }
    }
    return orders;
  }
}

// Instantiate and expose globally on window
window.aonaFirebase = new AonaFirebaseController();

